
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { FcGoogle } from "react-icons/fc";
import { FaFacebook, FaApple } from "react-icons/fa";
import { toast } from "sonner";
import { useAuth } from "@/providers/AuthProvider";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Loader2, Eye, EyeOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const SignUp = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      console.log("[SignUp] User already authenticated, redirecting to dashboard");
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage(null);
    
    try {
      console.log("[SignUp] Creating account for:", email);
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: name,
          },
          emailRedirectTo: `${window.location.origin}/dashboard`,
        },
      });
      
      if (error) {
        console.error("[SignUp] Sign up error:", error);
        setErrorMessage(error.message);
        toast.error("Sign-up failed: " + error.message);
      } else if (data?.user) {
        console.log("[SignUp] Sign up successful:", data.user.id);
        
        if (data.session) {
          toast.success("Account created and signed in successfully!");
          navigate("/dashboard", { replace: true });
        } else {
          toast.success("Account created! Check your email for confirmation.");
          navigate("/sign-in", { replace: true });
        }
      } else {
        console.error("[SignUp] Sign up response has no user");
        setErrorMessage("Sign up failed. Please try again.");
        toast.error("Sign up failed. Please try again.");
      }
    } catch (error: any) {
      console.error("[SignUp] Sign up exception:", error);
      setErrorMessage(error.message || "Something went wrong. Please try again.");
      toast.error("Failed to create account. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSocialSignUp = async (provider: "google" | "facebook" | "apple") => {
    setSocialLoading(provider);
    setErrorMessage(null);
    
    try {
      console.log(`[SignUp] Starting ${provider} sign up flow`);
      
      const redirectTo = `${window.location.origin}/dashboard`;
      console.log(`[SignUp] Redirect URL: ${redirectTo}`);
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo,
          // Add these query params for Google to ensure we get a refresh token
          queryParams: provider === 'google' ? {
            access_type: 'offline',
            prompt: 'consent',
          } : undefined
        },
      });
      
      if (error) {
        console.error(`[SignUp] ${provider} sign up error:`, error);
        setErrorMessage(error.message);
        toast.error(`${provider} sign-up failed: ` + error.message);
      } else if (data?.url) {
        console.log(`[SignUp] ${provider} redirect URL:`, data.url);
        // No need to redirect manually - Supabase will handle it
      } else {
        console.error(`[SignUp] No redirect URL returned from ${provider} auth`);
        setErrorMessage(`${provider} authentication failed. Please try again.`);
        toast.error(`${provider} authentication failed. Please try again.`);
      }
    } catch (error: any) {
      console.error(`[SignUp] ${provider} sign up exception:`, error);
      setErrorMessage(error.message || "Something went wrong. Please try again.");
      toast.error("Authentication error. Please try again.");
    } finally {
      setSocialLoading(null);
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-12 flex flex-col items-center">
      <div className="w-full max-w-md p-8 space-y-8 bg-white rounded-xl shadow-lg border border-heaven-gold/20">
        <div className="text-center">
          <h1 className="text-3xl font-playfair font-bold text-gray-800">Sign Up</h1>
          <p className="mt-2 text-gray-600">Create your Selah account</p>
        </div>

        {errorMessage && (
          <Alert variant="destructive" className="border-red-500 text-red-700 bg-red-50">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          <Button
            onClick={() => handleSocialSignUp("google")}
            variant="outline"
            className="w-full flex items-center justify-center gap-2 py-6"
            disabled={!!socialLoading}
          >
            {socialLoading === "google" ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Connecting...</span>
              </>
            ) : (
              <>
                <FcGoogle className="h-5 w-5" />
                <span>Continue with Google</span>
              </>
            )}
          </Button>
          
          <Button
            onClick={() => handleSocialSignUp("facebook")}
            variant="outline"
            className="w-full flex items-center justify-center gap-2 py-6 bg-[#1877F2] text-white hover:bg-[#0d65d9]"
            disabled={!!socialLoading}
          >
            {socialLoading === "facebook" ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Connecting...</span>
              </>
            ) : (
              <>
                <FaFacebook className="h-5 w-5" />
                <span>Continue with Facebook</span>
              </>
            )}
          </Button>
          
          <Button
            onClick={() => handleSocialSignUp("apple")}
            variant="outline"
            className="w-full flex items-center justify-center gap-2 py-6 bg-black text-white hover:bg-gray-800"
            disabled={!!socialLoading}
          >
            {socialLoading === "apple" ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Connecting...</span>
              </>
            ) : (
              <>
                <FaApple className="h-5 w-5" />
                <span>Continue with Apple</span>
              </>
            )}
          </Button>
          
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or continue with email</span>
            </div>
          </div>
          
          <form onSubmit={handleEmailSignUp} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500">Password must be at least 6 characters long</p>
            </div>
            
            <Button
              type="submit"
              variant="gold"
              className="w-full py-6"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating Account...
                </>
              ) : (
                "Create Account"
              )}
            </Button>
          </form>
        </div>
        
        <div className="text-center mt-4">
          <p className="text-sm text-gray-600">
            Already have an account?{" "}
            <a
              href="/sign-in"
              className="font-medium text-heaven-gold hover:text-heaven-dark-gold"
            >
              Sign in
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
